import { useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { StopInfo, BusEta } from '@/lib/api'

interface MapViewProps {
  stops: StopInfo[]
  selectedStop?: StopInfo | null
  buses?: BusEta[]
  focusedBus?: BusEta | null
  onStopSelect?: (stop: StopInfo) => void
  lineId: string
  lang: 'el' | 'en'
  showRoute?: boolean
}

const stopIcon = new L.DivIcon({
  className: 'map-marker stop-marker',
  html: '<div class="marker-inner"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8]
})

const selectedStopIcon = new L.DivIcon({
  className: 'map-marker stop-marker selected',
  html: '<div class="marker-inner"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12]
})

const busIcon = new L.DivIcon({
  className: 'map-marker bus-marker',
  html: '<div class="marker-inner">🚌</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14]
})

function FitBounds({ stops, selectedStop, buses, focusedBus }: { stops: StopInfo[]; selectedStop?: StopInfo | null; buses?: BusEta[]; focusedBus?: BusEta | null }) {
  const map = useMap()
  const hasFittedInitial = useRef(false)

  useEffect(() => {
    if (focusedBus && focusedBus.latitude && focusedBus.longitude && selectedStop) {
      const points: [number, number][] = [
        [parseFloat(selectedStop.StopLat), parseFloat(selectedStop.StopLng)],
        [focusedBus.latitude, focusedBus.longitude]
      ]
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [60, 60] })
      return
    }

    if (hasFittedInitial.current) return
    if (stops.length === 0) return

    const closestBus = buses?.[0]
    if (selectedStop && closestBus?.latitude && closestBus?.longitude) {
      const points: [number, number][] = [
        [parseFloat(selectedStop.StopLat), parseFloat(selectedStop.StopLng)],
        [closestBus.latitude, closestBus.longitude]
      ]
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [60, 60] })
    } else if (selectedStop) {
      map.setView([parseFloat(selectedStop.StopLat), parseFloat(selectedStop.StopLng)], 15)
    } else {
      const points: [number, number][] = stops.map(s => [
        parseFloat(s.StopLat),
        parseFloat(s.StopLng)
      ])
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [40, 40] })
    }
    hasFittedInitial.current = true
  }, [stops, selectedStop, buses, focusedBus, map])

  return null
}

export function MapView({
  stops,
  selectedStop,
  buses,
  focusedBus,
  onStopSelect,
  lineId,
  lang,
  showRoute = true
}: MapViewProps) {
  const getStopName = (stop: StopInfo) =>
    lang === 'en' ? (stop.StopDescrEng || stop.StopDescr) : stop.StopDescr

  const routeCoordinates: [number, number][] = stops.map(s => [
    parseFloat(s.StopLat),
    parseFloat(s.StopLng)
  ])

  const defaultCenter: [number, number] = selectedStop
    ? [parseFloat(selectedStop.StopLat), parseFloat(selectedStop.StopLng)]
    : stops.length > 0
      ? [parseFloat(stops[0].StopLat), parseFloat(stops[0].StopLng)]
      : [37.9838, 23.7275]

  return (
    <div className="map-view-container">
      <div className="map-line-badge">
        <span>{lineId}</span>
      </div>

      <MapContainer
        center={defaultCenter}
        zoom={14}
        className="map-view"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds stops={stops} selectedStop={selectedStop} buses={buses} focusedBus={focusedBus} />

        {showRoute && routeCoordinates.length > 1 && (
          <Polyline
            positions={routeCoordinates}
            pathOptions={{
              color: '#3b82f6',
              weight: 4,
              opacity: 0.7,
              lineCap: 'round',
              lineJoin: 'round'
            }}
          />
        )}

        {stops.map(stop => (
          <Marker
            key={stop.StopCode}
            position={[parseFloat(stop.StopLat), parseFloat(stop.StopLng)]}
            icon={selectedStop?.StopCode === stop.StopCode ? selectedStopIcon : stopIcon}
            eventHandlers={{
              click: () => onStopSelect?.(stop)
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} className="map-tooltip">
              <div className="tooltip-content">
                <span className="tooltip-order">{stop.RouteStopOrder}</span>
                <span className="tooltip-name">{getStopName(stop)}</span>
              </div>
            </Tooltip>
          </Marker>
        ))}

        {buses?.map(bus => {
          if (!bus.latitude || !bus.longitude) return null
          return (
            <Marker
              key={bus.vehicleNumber}
              position={[bus.latitude, bus.longitude]}
              icon={busIcon}
            >
              <Tooltip direction="top" offset={[0, -14]} className="map-tooltip bus-tooltip">
                <div className="tooltip-content">
                  <span className="tooltip-bus">#{bus.vehicleNumber}</span>
                  <span className="tooltip-eta">{bus.etaMinutes} min</span>
                  <span className="tooltip-stops">{bus.stopsAway} stops away</span>
                </div>
              </Tooltip>
            </Marker>
          )
        })}
      </MapContainer>
    </div>
  )
}
